import { db } from '@/firebase/init'
import { InsuranceCustomerAccount, type IInsuranceCustomerAccount } from '@team-milva/lab-models/insuranceCustomerAccount'
import { useAccountStore } from '@/stores/accountStore'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { defineStore } from 'pinia'

// Read-only reference data — the insurance customer accounts belonging to the
// customer's own lab account, resolved by ID to a display name for the
// Orders list and search (MIL-334 Phase 3/4, IOrder.insurancePartnerID).
// Insurance customer accounts are managed entirely in lab-management; this store never writes.
export const useInsuranceCustomerAccountStore = defineStore('insuranceCustomerAccount', {
  state: () => ({
    allInsuranceCustomerAccounts: [] as InsuranceCustomerAccount[],
    unsubscribeAllInsuranceCustomerAccounts: null as (() => void) | null,
  }),

  actions: {
    loadInsuranceCustomerAccounts(): Promise<InsuranceCustomerAccount[]> {
      if (this.allInsuranceCustomerAccountsLoaded) {
        return Promise.resolve(this.allInsuranceCustomerAccounts)
      }

      const labAccount = useAccountStore().labAccount

      return new Promise((resolve) => {
        if (!labAccount) {
          return resolve([])
        }

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'InsuranceCustomerAccounts'),
          where('labAccountID', '==', labAccount.id)
        )

        let resolved = false
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const insuranceCustomerAccounts: InsuranceCustomerAccount[] = []
          querySnapshot.forEach((docSnap) => {
            insuranceCustomerAccounts.push(new InsuranceCustomerAccount(docSnap.data() as IInsuranceCustomerAccount))
          })
          this.allInsuranceCustomerAccounts = insuranceCustomerAccounts
          this.unsubscribeAllInsuranceCustomerAccounts = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(insuranceCustomerAccounts)
          }
        })
      })
    },

    resetStore(): void {
      this.allInsuranceCustomerAccounts = []

      if (this.unsubscribeAllInsuranceCustomerAccounts) {
        this.unsubscribeAllInsuranceCustomerAccounts()
        this.unsubscribeAllInsuranceCustomerAccounts = null
      }
    },
  },

  getters: {
    allInsuranceCustomerAccountsLoaded: (state) => !!state.unsubscribeAllInsuranceCustomerAccounts,

    getInsuranceCustomerAccountById:
      (state) =>
      (insuranceCustomerAccountID: string): InsuranceCustomerAccount | undefined =>
        state.allInsuranceCustomerAccounts.find((x) => x.id === insuranceCustomerAccountID),
  },
})
